import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Hammer, HardHat, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WorkforcePellCallout, highlights OBBBA's new Workforce Pell Grants, which
 * (for the first time) fund short-term job-training in skilled trades and
 * blue-collar fields. Reused on financial pages, trade pathway pages, and
 * student/parent dashboards so the opportunity is discoverable everywhere.
 *
 *   <WorkforcePellCallout />            // full card
 *   <WorkforcePellCallout compact />    // slim banner
 */
export default function WorkforcePellCallout({ compact = false }) {
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    // Workforce Pell is an OBBBA (July 1, 2026) provision. Today's *active*
    // ruleset may still be Pre-OBBBA (which has it disabled), so query the
    // forward-looking ruleset a student enrolling now will actually face.
    const planningDate = (() => {
      const today = new Date().toISOString().slice(0, 10);
      return today > '2026-07-01' ? today : '2026-07-01';
    })();
    axios.get(`${API}/federal-loan-rules/active/public?for_date=${planningDate}`)
      .then((r) => setEnabled(r.data?.rules?.workforce_pell_enabled !== false))
      .catch(() => { /* default to showing it */ });
  }, []);

  if (!enabled) return null;

  if (compact) {
    return (
      <Link
        to="/app/paths/trades"
        className="flex items-center gap-2 p-3 rounded-lg bg-teal-500/10 border border-teal-500/30 hover:border-teal-400/50 transition-colors group"
        data-testid="workforce-pell-callout-compact"
      >
        <HardHat className="w-4 h-4 text-teal-300 flex-shrink-0" />
        <p className="text-teal-100 text-xs flex-1">
          <strong>New under OBBBA:</strong> Workforce Pell Grants now fund short-term skilled-trade &amp; blue-collar training.
        </p>
        <ArrowRight className="w-4 h-4 text-teal-300 group-hover:translate-x-0.5 transition-transform" />
      </Link>
    );
  }

  return (
    <div
      className="relative overflow-hidden p-5 rounded-xl border border-teal-500/30 bg-gradient-to-br from-teal-900/30 to-emerald-900/20 backdrop-blur-xl"
      data-testid="workforce-pell-callout"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-500/20 ring-1 ring-teal-400/30 flex items-center justify-center flex-shrink-0">
          <HardHat className="w-5 h-5 text-teal-300" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-teal-100 font-semibold">College isn&apos;t the only funded path</h4>
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-teal-500/20 text-teal-200 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> New 2026
            </span>
          </div>
          <p className="text-teal-50/90 text-sm leading-relaxed mb-3">
            Under the <strong>One Big Beautiful Bill Act</strong>, new <strong>Workforce Pell Grants</strong> now
            cover short-term (roughly 8-15 week) job-training programs in skilled trades and blue-collar fields,             welding, HVAC, electrical, commercial driving, healthcare support, and more. That means real federal
            aid for high-demand careers that don&apos;t require a 4-year degree.
          </p>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/app/paths/trades"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-teal-500/20 hover:bg-teal-500/30 text-teal-100 border border-teal-400/30 transition-colors"
              data-testid="workforce-pell-trades-link"
            >
              <Hammer className="w-4 h-4" /> Explore Skilled Trades <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/app/paths/workforce"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-slate-700/40 hover:bg-slate-700/60 text-slate-100 border border-slate-600/40 transition-colors"
              data-testid="workforce-pell-workforce-link"
            >
              Direct-to-Workforce <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
